import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, MapPin, Clock, Zap, Grid, DollarSign,
  CheckCircle, XCircle, AlertCircle, Loader2, FileText, X
} from 'lucide-react';
import { ReservationDetail } from '../types';
import { reservationApi } from '../services/api';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateRange } from '../utils/fairStatus'; // Issue #1672

export default function ReservationDetailsModal() {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();

  const [reservation, setReservation] = useState<ReservationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadReservation = async () => {
      if (!reservationId) {
        setError('Invalid reservation ID');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await reservationApi.getReservationDetails(parseInt(reservationId, 10));
        if (isMounted) {
          setReservation(data);
        }
      } catch (err: unknown) {
        if (isMounted) {
          setError(err.response?.data?.detail || 'Failed to load reservation');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadReservation();

    return () => {
      isMounted = false;
    };
  }, [reservationId]);

  const handleCancel = async () => {
    if (!reservationId) return;

    try {
      setIsCancelling(true);
      await reservationApi.cancelReservation(parseInt(reservationId, 10), cancelReason || undefined);
      setReservation(prev => prev ? { ...prev, status: 'cancelled' } : null);
      setShowCancelConfirm(false);
    } catch (err: unknown) {
      setError(err.response?.data?.detail || 'Failed to cancel reservation');
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            Pending Approval
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const canCancel = reservation && reservation.status !== 'cancelled';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-center py-12 bg-red-50 rounded-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error || 'Reservation not found'}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Reservations
        </button>
        {getStatusBadge(reservation.status)}
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-white">
          <p className="text-teal-100 text-sm mb-1">Reservation</p>
          <h1 className="text-2xl font-bold">RES-{reservation.id.toString().padStart(6, '0')}</h1>
          <p className="text-teal-100 mt-2">
            Created on {new Date(reservation.created_at || '').toLocaleDateString()}
          </p>
        </div>

        {/* Details */}
        <div className="p-6 space-y-6">
          {/* Fair Info */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Fair Details</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">{reservation.fair_name}</h3>
              {(reservation.fair_city || reservation.fair_address) && (
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>
                    {reservation.fair_address && `${reservation.fair_address}, `}
                    {reservation.fair_city}, {reservation.fair_state}
                  </span>
                </div>
              )}
              {reservation.fair_start_date && reservation.fair_end_date && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {formatDateRange(reservation.fair_start_date, reservation.fair_end_date)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Slot Info */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Slot Details</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">Slot #{reservation.slot_number}</h3>
                {reservation.slot_type && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    reservation.slot_type === 'premium' ? 'bg-purple-100 text-purple-700' :
                    reservation.slot_type === 'preferred' ? 'bg-moonbone text-primary-dark' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {reservation.slot_type}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {reservation.slot_dimensions && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Grid className="w-4 h-4" />
                    <span>{reservation.slot_dimensions}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Zap className={`w-4 h-4 ${reservation.slot_has_power ? 'text-yellow-500' : ''}`} />
                  <span>{reservation.slot_has_power ? 'Power available' : 'No power'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation Info */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Reservation Info</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span>
                  {reservation.reservation_type === 'full_duration' ? 'Full Duration' : 'Per-Day'}
                </span>
              </div>
              {reservation.reservation_type === 'per_day' && reservation.selected_dates && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {reservation.selected_dates.map(date => (
                    <span key={date} className="px-2 py-1 bg-teal-100 text-primary-dark rounded text-sm">
                      {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {reservation.notes && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Notes
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-600 whitespace-pre-line">{reservation.notes}</p>
              </div>
            </div>
          )}

          {/* Pricing */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Total Price</span>
              </div>
              <span className="text-2xl font-bold text-secondary">
                {formatCurrency(reservation.price_paid)} {/* Issue #905: Decimal.js formatting */}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Payment status: {reservation.payment_status}
            </p>
          </div>
        </div>

        {/* Actions */}
        {canCancel && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="w-full px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              Cancel Reservation
            </button>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Reservation?</h3>
              <button onClick={() => setShowCancelConfirm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel this reservation? This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep Reservation
              </button>
              <button
                onClick={handleCancel}
                disabled={isCancelling}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
